import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuthUser } from 'auth/hooks';
import { FormProvider } from 'core/states';
import { IAttributeForm } from 'quotes/components/ExportSkuPart/types';
import { VendorQuoteAttachToType, VendorQuoteDuplicateOutcome } from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { TImportQuoteActions } from 'quotes/hooks';
import {
  IImportedQuoteData,
  IImportVendorQuoteForm,
  IVendorContactSignal,
  IVendorMappingSignal,
  IVendorQuote,
  IVendorQuoteNumberSignal,
  IVendorQuoteRevisionSignal,
} from 'quotes/types';

import ImportVendorQuoteFormView from './ImportVendorQuoteFormView';
import { ImportVendorQuoteSteps } from './ImportVendorQuoteSteps';
import { importVendorQuoteSchema } from './schema';
import {
  getDefaultFormValues,
  isVendorMappingMappedVendorState,
  isVendorMappingPromptState,
  isVendorMappingRememberedState,
  isVendorQuoteNumberPromptState,
  isVendorQuoteNumberResolvedState,
} from './utils';

interface IProps {
  disabled?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  quote?: IVendorQuote;
  importedQuoteData?: IImportedQuoteData;
  // Identifies the import `importedQuoteData`/`vendorContact` were extracted from, so a decision
  // taken for one import is never applied to the next.
  extractedImportId?: number | null;
  vendorContact?: IVendorContactSignal | null;
  // Absent/null (BE not deployed yet) behaves exactly as before this feature existed.
  vendorMapping?: IVendorMappingSignal | null;
  // Same contract: absent/null keeps the extracted number and today's flow.
  vendorQuoteNumber?: IVendorQuoteNumberSignal | null;
  // Never vetoes the auto-submit; only prefills step 1's optional revision when step 1 renders.
  vendorQuoteRevision?: IVendorQuoteRevisionSignal | null;
  // The finalize failed and the retry is the user's to make. The headless path has no Confirm of
  // its own, so the contact prompt takes over — with the very values the auto-submit used.
  isFinalizeFailed?: boolean;
  onImport?: (data: IImportVendorQuoteForm, attributes?: IAttributeForm | null) => void;
  // Gated (Attributes) new-import path: the duplicate check that runs before the capture step, and
  // the import fired straight from its Confirm (false = it failed, stay on the step).
  onResolveDuplicate: (data: IImportVendorQuoteForm) => Promise<VendorQuoteDuplicateOutcome>;
  // `importActions` = the wizard's own finalize mutations (see ImportVendorQuoteSteps.onConfirm).
  onStartImport: (
    attributes?: IAttributeForm | null,
    importActions?: TImportQuoteActions
  ) => Promise<boolean | void>;
  defaultValues?: Partial<IImportVendorQuoteForm>;
  hiddenFields?: TImportVendorQuoteHiddenFields;
}

export type TImportVendorQuoteHiddenFields = {
  project?: boolean;
  jobName?: boolean;
  attachTo?: boolean;
};

export const ImportVendorQuoteForm = ({
  disabled = false,
  isOpen = false,
  onClose,
  quote,
  importedQuoteData,
  extractedImportId = null,
  vendorContact,
  vendorMapping,
  vendorQuoteNumber,
  vendorQuoteRevision,
  isFinalizeFailed = false,
  onImport,
  onResolveDuplicate,
  onStartImport,
  defaultValues,
}: IProps) => {
  const autoSubmittedRef = useRef(false);
  // The import the duplicate check has already run for, NOT a plain boolean: the drawer close is
  // delayed by a second, so a second upload can land while this is still mounted and must run its
  // own check rather than inherit the previous import's decision.
  const duplicateCheckedRef = useRef<number | null>();

  const [duplicateCheck, setDuplicateCheck] = useState<{
    importId: number | null;
    outcome: VendorQuoteDuplicateOutcome;
  } | null>(null);

  const { user } = useAuthUser();

  const formDefaultValues = useMemo(
    () =>
      getDefaultFormValues(
        quote,
        importedQuoteData,
        defaultValues,
        vendorContact,
        vendorMapping,
        vendorQuoteRevision
      ),
    [quote, importedQuoteData, defaultValues, vendorContact, vendorMapping, vendorQuoteRevision]
  );

  // Contact available (resolved OR re-import into an existing quote) -> auto-import, no form shown.
  // Not available (unresolved new import, OR a resolved signal without a contactId) -> a form with
  // ONLY the vendor contact field. Non-contact fields are always defaulted (project = context OR
  // the BE-resolved quote_data.project_id). A missing signal (undefined) is treated as unresolved.
  const isResolved = vendorContact?.resolved === true && vendorContact?.contactId != null;
  const isReImport = vendorContact === null;

  // Additive to the tri-state above, never a reinterpretation of it: the BE keeps sending
  // `vendorContact = {resolved: false}` in the prompt states so a pre-feature FE still renders the
  // contact-only modal — except on a re-import, which keeps `null` (contract §19.2).
  //
  // Alternate importers are the cohort the vendor AP snapshot is written for, so only they are
  // asked for a vendor — and only while the mapping still needs one. `vendorRequired` is the ONLY
  // source for "alternate" here; it is per user, with no company-level flag behind it.
  const isVendorRequired =
    isVendorMappingPromptState(vendorMapping) && true === vendorMapping?.vendorRequired;
  // A stored vendor is only ever prefilled while it still resolves, and the finalize refuses to
  // re-point it — so it is shown, not offered for change.
  const isVendorLocked = isVendorRequired && Boolean(vendorMapping?.prefill?.vendor);
  // A `mapped` vendor is never asked, but step 1 still shows it beside the contact and number.
  const isVendorMapped = isVendorMappingMappedVendorState(vendorMapping);
  // The extraction found no vendor quote number, and the finalize 422s without one — for classic
  // and alternate importers alike.
  const isVendorQuoteNumberRequired = isVendorQuoteNumberPromptState(vendorQuoteNumber);
  const isVendorQuoteNumberLocked = isVendorQuoteNumberResolvedState(vendorQuoteNumber);
  const showVendorQuoteNumber = isVendorQuoteNumberRequired || isVendorQuoteNumberLocked;
  // When the number is the only thing owed, the detected contact is shown, not offered for change.
  const isContactLocked = isVendorQuoteNumberRequired && !isVendorRequired && isResolved;
  // Null on a re-import, whose revision the BE saves exactly as posted, so it is not offered there.
  const showVendorQuoteRevision = vendorQuoteRevision != null;

  // An OWED VENDOR or an OWED NUMBER vetoes the headless path: the finalize 422s without either.
  // For a classic importer the mapping state is informational — the BE derives their
  // `vendorContact.resolved` from the extracted contact — so a resolved contact with a number keeps
  // auto-submitting, even on an unmapped extractor.
  // A finalize that already failed also vetoes it: re-running the headless submit would loop on the
  // same error, so the contact-only prompt renders instead and its Confirm re-posts the payload —
  // which the BE resumable finalize applies to the quote the failed attempt created.
  const isAutoImport =
    !isVendorRequired &&
    !isVendorQuoteNumberRequired &&
    !isFinalizeFailed &&
    (isResolved || isReImport);

  // Drives the vendor field's helper text only: `no_extractor` prompts for a vendor the finalize
  // will use but never store.
  const isVendorMappingRemembered = isVendorMappingRememberedState(vendorMapping);

  // A re-import into a quote whose Attributes this user cannot edit never offers the capture step:
  // the BE finalize rejects attribute keys for such a quote (403) while the re-import itself stays
  // allowed. Dropping the step routes the import through the headless path, which sends none.
  const isAttributesLocked = isReImport && VendorQuoteHelper.isAttributesEditBlocked(quote);

  const isCostingProfileImport =
    defaultValues?.attachTo === VendorQuoteAttachToType.COSTING_PROFILE;
  const isAttributesStepEnabled =
    !isCostingProfileImport &&
    !isAttributesLocked &&
    VendorQuoteHelper.isImportAttributesStepAvailable({ user });

  // A gated new import whose contact is already known can be dedup-checked before anything is put
  // on screen; the re-import path can't, since the check would match the quote being imported into.
  // An owed number moves the check to the contact step's Next: run now, it would dedupe on a blank
  // number instead of the one the user is about to type.
  const needsDuplicateCheck = isAttributesStepEnabled && isResolved && !isVendorQuoteNumberRequired;
  const duplicateOutcome =
    duplicateCheck && duplicateCheck.importId === extractedImportId ? duplicateCheck.outcome : null;
  const isDuplicateCheckSettled =
    VendorQuoteDuplicateOutcome.NONE === duplicateOutcome ||
    VendorQuoteDuplicateOutcome.CANCELED === duplicateOutcome;

  const onSubmit = () => (data: IImportVendorQuoteForm) => onImport(data);

  useEffect(() => {
    if (!isOpen) {
      autoSubmittedRef.current = false;

      return;
    }

    // A gated user must never be headlessly auto-imported, so wait for the auth user to resolve.
    if (!user || isAttributesStepEnabled) {
      return;
    }

    // Never headlessly submit without a handler or a real contact id — the payload requires a
    // non-null numeric contact id, so a null id here would produce an invalid import.
    const canAutoImport = onImport && formDefaultValues?.contact?.id != null;

    if (isAutoImport && !autoSubmittedRef.current && canAutoImport) {
      autoSubmittedRef.current = true;
      onImport(formDefaultValues as IImportVendorQuoteForm);
    }
  }, [isOpen, user, isAttributesStepEnabled, isAutoImport, formDefaultValues, onImport]);

  useEffect(() => {
    if (!isOpen || !needsDuplicateCheck || duplicateCheckedRef.current === extractedImportId) {
      return;
    }

    duplicateCheckedRef.current = extractedImportId;
    onResolveDuplicate(formDefaultValues as IImportVendorQuoteForm)
      .then((outcome) => setDuplicateCheck({ importId: extractedImportId, outcome }))
      // Fail open, matching the check's own policy: an unexpected throw must not leave the form
      // rendering null forever.
      .catch(() =>
        setDuplicateCheck({
          importId: extractedImportId,
          outcome: VendorQuoteDuplicateOutcome.NONE,
        })
      );
  }, [isOpen, needsDuplicateCheck, extractedImportId, formDefaultValues, onResolveDuplicate]);

  // Re-running the check with the contact just picked on the wizard's contact step. Staying on the
  // step (false) covers both a duplicate the user canceled and one they chose to reuse.
  const onContactNext = useCallback(
    async (data: IImportVendorQuoteForm) => {
      // The vendor must travel with the contact: this call also builds the payload the wizard's
      // Confirm finalizes with (validatedImportRef), so a vendor left behind here is a vendor the
      // finalize never receives — and the alternate cohort's finalize 422s without one.
      const outcome = await onResolveDuplicate({
        ...formDefaultValues,
        contact: data.contact,
        vendorLocation: data.vendorLocation,
        // A typed vendor quote number must reach both the duplicate check and the payload the
        // Attributes Confirm finalizes with, or the finalize 422s on the blank extracted one.
        externalNumber: data.externalNumber,
        revisionNumber: data.revisionNumber,
      } as IImportVendorQuoteForm);

      return VendorQuoteDuplicateOutcome.NONE === outcome;
    },
    [formDefaultValues, onResolveDuplicate]
  );

  const onConfirmImport = useCallback(
    (
      _: IImportVendorQuoteForm,
      attributes?: IAttributeForm | null,
      importActions?: TImportQuoteActions
    ) => onStartImport(attributes, importActions),
    [onStartImport]
  );

  if (!isOpen) {
    return null;
  }

  if (isAttributesStepEnabled) {
    // Re-import into the current quote: no upfront check, and the attributes ride the finalize
    // payload through the existing validate -> import chain. A re-import that still owes a vendor
    // (no usable mapping, contract §19) takes the wizard below instead: its contact step is the
    // vendor field's home, and its Confirm reports a failed finalize so the step stays retryable.
    // The duplicate check stays off there — the hook skips it for a re-import.
    if (isReImport && !isVendorRequired) {
      return (
        <ImportVendorQuoteSteps
          isOpen={isOpen}
          onClose={onClose}
          quote={quote}
          formDefaultValues={formDefaultValues}
          disabled={disabled}
          onConfirm={onImport}
        />
      );
    }

    // The check is still running, or the user reused the existing quote and the drawer is already
    // closing — nothing to render either way.
    if (needsDuplicateCheck && !isDuplicateCheckSettled) {
      return null;
    }

    return (
      <ImportVendorQuoteSteps
        isOpen={isOpen}
        onClose={onClose}
        quote={quote}
        formDefaultValues={formDefaultValues}
        disabled={disabled}
        isFirstImport={!isReImport}
        // Canceling the duplicate dialog hands the vendor contact back to the user, so the wizard
        // gains the contact step it would otherwise have skipped. `isVendorRequired` forces it too:
        // the contact step is the vendor field's only home in the wizard, so skipping it while a
        // vendor is owed would leave the user in front of a finalize that can only 422.
        withContactStep={
          !isResolved ||
          VendorQuoteDuplicateOutcome.CANCELED === duplicateOutcome ||
          isVendorRequired ||
          isVendorQuoteNumberRequired
        }
        // Keyed off the mapping state, not off the contact step: a CANCELED duplicate on `mapped`
        // re-opens the contact step, and that one must never ask for a vendor (only show it).
        withVendorLocation={isVendorRequired}
        withVendorQuoteNumber={showVendorQuoteNumber}
        isVendorQuoteNumberLocked={isVendorQuoteNumberLocked}
        withVendorQuoteRevision={showVendorQuoteRevision}
        isContactLocked={isContactLocked}
        isVendorLocked={isVendorLocked}
        isVendorMapped={isVendorMapped}
        isVendorMappingRemembered={isVendorMappingRemembered}
        onContactNext={onContactNext}
        onConfirm={onConfirmImport}
      />
    );
  }

  // Auto-import submits headlessly (drawer never shown). Only the unresolved path renders, as a
  // contact-only form; the full wizard is intentionally never mounted for a vendor-quote import.
  if (isAutoImport) {
    return null;
  }

  // Job name and project are BE-required to create the quote's job (setUpBom needs BOTH
  // project_id + job_name, else quote creation fails the "Invalid job." ACL): when no context
  // (job page / re-import / extraction) defaulted them, the fields must be rendered — and
  // project made schema-required — or the user can never satisfy the validation.
  const showJobName = !formDefaultValues?.jobName;
  const showProject = !formDefaultValues?.project?.id;

  // An alternate importer whose cohort has no Attributes wizard (export off, a costing-profile
  // import, or a re-import into a quote whose Attributes are locked) still has to answer the
  // vendor, or the finalize 422s — so the field rides this form too.
  return (
    <FormProvider
      schema={importVendorQuoteSchema({
        isProjectRequired: showProject,
        isVendorRequired,
        isVendorQuoteNumberRequired,
        isVendorQuoteRevisionShown: showVendorQuoteRevision,
      })}
      defaultValues={formDefaultValues}
    >
      <ImportVendorQuoteFormView
        disabled={disabled}
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onSubmit}
        showJobName={showJobName}
        showProject={showProject}
        showVendorLocation={isVendorRequired}
        showVendorQuoteNumber={showVendorQuoteNumber}
        isVendorQuoteNumberLocked={isVendorQuoteNumberLocked}
        showVendorQuoteRevision={showVendorQuoteRevision}
        isContactLocked={isContactLocked}
        isVendorLocked={isVendorLocked}
        isVendorMapped={isVendorMapped}
        isVendorMappingRemembered={isVendorMappingRemembered}
      />
    </FormProvider>
  );
};
